import { Building, Industry, ToolKit, Grid } from "@carbon/icons-react";
import type { CarbonIconType } from "@carbon/icons-react/lib/CarbonIcon";
import { CategoryType } from "../../types";

export type TabKey = CategoryType | "ASSIGNED_CATEGORIES";

export interface OpportunityTabCard {
  key: TabKey;
  title: string;
  count: number;
  Icon: CarbonIconType;
}

/** The `t` returned by `useTranslation()` — typed loosely (same convention every other row/tab
 * helper in this app uses) since every call site here only ever calls it with a plain key. */
type TFunc = (key: string) => string;

interface CountsLike {
  INDIRECT_GOODS?: number;
  DIRECT_GOODS?: number;
  SERVICES?: number;
}

/**
 * Builds the dashboard's counts-by-type tab strip. Pulled out of `OpportunityDashboard.tsx` so
 * the filtering rule is unit-testable in isolation from the AssignedCategories/company-profile
 * hook stack the dashboard itself depends on.
 *
 * One tab per `CategoryType` whose count is > 0, plus an always-present "Assigned Categories"
 * tab last. This is a verbatim port of the pre-Carbon `tabCards` derivation in
 * `OpportunityDashboard.tsx` — `e2e/opportunities.spec.ts` pins that a dataset with every
 * type-count at 0 collapses this to exactly one tab ("Assigned Categories"), so the filter here
 * must stay `count > 0`, never `>= 0` or unconditional.
 */
export const buildOpportunityTabCards = (
  counts: CountsLike | undefined,
  assignedCategoriesCount: number,
  t: TFunc
): OpportunityTabCard[] => {
  const typeCards: OpportunityTabCard[] = [
    {
      key: CategoryType.INDIRECT_GOODS,
      title: t("opportunities.dashboard.indirectGoods"),
      count: counts?.INDIRECT_GOODS || 0,
      Icon: Building,
    },
    {
      key: CategoryType.DIRECT_GOODS,
      title: t("opportunities.dashboard.directGoods"),
      count: counts?.DIRECT_GOODS || 0,
      Icon: Industry,
    },
    {
      key: CategoryType.SERVICES,
      title: t("opportunities.dashboard.services"),
      count: counts?.SERVICES || 0,
      Icon: ToolKit,
    },
  ];

  return [
    ...typeCards.filter((card) => card.count > 0),
    {
      key: "ASSIGNED_CATEGORIES",
      title: t("opportunities.dashboard.assignedCategories"),
      count: assignedCategoriesCount,
      Icon: Grid,
    },
  ];
};

/**
 * Resolves which tab key should actually be active: `activeTab` if it still exists in the
 * (possibly-filtered) `tabCards`, otherwise the first card, otherwise the always-present
 * "Assigned Categories" fallback. Same fallback chain the pre-Carbon component's own
 * `resolvedActiveTab` used — kept as a pure function so it can be unit-tested against the
 * count>0 filtering above without rendering the dashboard.
 */
export const resolveActiveTabKey = (
  tabCards: OpportunityTabCard[],
  activeTab: TabKey
): TabKey =>
  tabCards.some((tab) => tab.key === activeTab)
    ? activeTab
    : (tabCards[0]?.key ?? "ASSIGNED_CATEGORIES");
